/* eslint max-statements: ["error", {max: 25}] -- applies to entire file */
/* eslint max-lines: ["error", {max: 411}] -- due to long complex hook */
/* eslint max-lines-per-function: ["error", {max: 321}] -- this applies for the entire file */
import { useCallback, useState } from "react";

import { jwtDecode } from "jwt-decode";
import { isNil } from "lodash";
import { Platform } from "react-native";
import type {
  Credentials,
  MfaAuthenticator,
  MfaChallengeResult,
  MfaGetAuthenticatorsParameters,
  PasswordRealmParameters,
  ResetPasswordParameters,
} from "react-native-auth0";
import { CredentialsManagerErrorCodes, useAuth0 } from "react-native-auth0";
import type { Auth0ContextInterface } from "react-native-auth0/src/hooks/Auth0Context";

import { AnalyticsFieldName } from "@/constants/analytics/Analytics";
import {
  LoginAnalyticsEvent,
  LoginAnalyticsPageView,
} from "@/constants/analytics/Login";
import { LogoutReason } from "@/constants/Login";
import { LoginScreenPath, ScreenPath } from "@/constants/Navigation";
import ProcessEnvironment from "@/constants/ProcessEnvironment";
import { DeviceType } from "@/constants/User";
import { router } from "@/crossPlatform/expo-router";
import { useAnalytics } from "@/services/Analytics";
import { captureException } from "@/services/AppMonitor";
import { WebAuth0AuthorizeProps } from "@/services/Auth";
import { useUpdateUserDeviceLoginStateMutation } from "@/store/api/user";
import { useAppStartupDispatch } from "@/store/reducers/appStartup";
import { useAuthDispatch } from "@/store/reducers/auth";
import { useMFADispatch, useMFASelector } from "@/store/reducers/mfa";
import type { SerializableAuthResponse } from "@/utils/auth.util";
import {
  getFailedLoginReason,
  getUserFacingLoginErrorResponse,
  wrapAuth0Call,
} from "@/utils/auth.util";
import { getPreferredAuthenticator } from "@/utils/mfa.util";
import { isWeb } from "@/utils/platform.util";

interface UseAuth {
  authorize: () => Promise<SerializableAuthResponse<Credentials>>;
  authorizeWithOOB: (
    bindingCode?: string,
  ) => Promise<SerializableAuthResponse<Credentials | void>>;
  clearCredentials: (options?: {
    errorScenario?: string;
  }) => Promise<SerializableAuthResponse<void>>;
  clearWebSession: (
    logoutReason: LogoutReason,
  ) => Promise<SerializableAuthResponse<void> | undefined>;
  error: Auth0ContextInterface["error"];
  getAuthenticators: (
    params: MfaGetAuthenticatorsParameters,
  ) => Promise<SerializableAuthResponse<MfaAuthenticator[]>>;
  getCredentials: (options?: {
    errorContext?: { [key: string]: unknown };
    errorScenario?: string;
    minTtl?: number;
    parameters?: Record<string, unknown>;
    scope?: string;
    shouldForceRefresh?: boolean;
  }) => Promise<SerializableAuthResponse<Credentials>>;
  hasValidCredentials: (
    minTtl?: number,
  ) => Promise<SerializableAuthResponse<boolean>>;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithPasswordRealm: (
    params: Pick<PasswordRealmParameters, "password" | "username">,
  ) => Promise<SerializableAuthResponse<Credentials | void>>;
  logout: (
    reason: LogoutReason,
  ) => Promise<SerializableAuthResponse<void> | undefined>;
  passwordResetUrl: string | undefined;
  preLoginCleanup: () => Promise<SerializableAuthResponse<void> | undefined>;
  resetPassword: (
    email: ResetPasswordParameters["email"],
  ) => Promise<SerializableAuthResponse<void>>;
}

const PasswordResetClaim = "https://app.equip.health/password_reset_ticket";

const useAuth = (): UseAuth => {
  const {
    authorize: authorizeAuth0,
    authorizeWithOOB: authorizeWithAuth0OOB,
    clearCredentials: clearAuth0Credentials,
    clearSession,
    error,
    getCredentials: getAuth0Credentials,
    hasValidCredentials: hasValidAuth0Credentials,
    isLoading,
    loginWithPasswordRealm: loginWithAuth0PasswordRealm,
    mfa,
    resetPassword: resetAuth0Password,
    user,
  } = useAuth0();
  const { track } = useAnalytics();
  const { setLogoutReason } = useAuthDispatch();
  const { setCanLoggedInUserSeeAppContent } = useAppStartupDispatch();

  const { resetMFAState, setMFAToken, setOOBCode } = useMFADispatch();
  const { mfaToken: mfaTokenFromState, oobCode: oobCodeFromState } =
    useMFASelector();

  const [updateUserDeviceLoginState] = useUpdateUserDeviceLoginStateMutation();
  const [passwordResetUrl, setPasswordResetUrl] = useState<string>();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const isAuthenticated = Boolean(user && !user[PasswordResetClaim]);

  const clearCredentials = useCallback(
    ({ errorScenario }: { errorScenario?: string } = {}) =>
      wrapAuth0Call({
        auth0Call: clearAuth0Credentials,
        errorScenario,
      }),
    [clearAuth0Credentials],
  );

  const logout = useCallback(
    async (reason: LogoutReason) => {
      setLogoutReason(reason);

      // JustPreLoginCleanup is preventive cleanup before login, not an actual logout
      if (reason === LogoutReason.JustPreLoginCleanup) {
        // clearing credentials sets `isAuthenticated` to `false`,
        // which will redirect the user to the Login page via Expo protected routes,
        // as defined in app/_layout.tsx
        return await clearCredentials();
      }

      // for actual logout scenarios, hide app content and track the event
      track(LoginAnalyticsEvent.Logout, { reason });
      router.replace(ScreenPath.Logout);
      setCanLoggedInUserSeeAppContent(false);
      return undefined;
    },
    [clearCredentials, setCanLoggedInUserSeeAppContent, setLogoutReason, track],
  );

  const preLoginCleanup = useCallback(
    () => logout(LogoutReason.JustPreLoginCleanup),
    [logout],
  );

  const getCredentials = useCallback(
    async ({
      errorContext,
      errorScenario,
      minTtl,
      parameters,
      scope,
      shouldForceRefresh,
    }: {
      errorContext?: { [key: string]: unknown };
      errorScenario?: string;
      minTtl?: number;
      parameters?: Record<string, unknown>;
      scope?: string;
      shouldForceRefresh?: boolean;
    } = {}) => {
      const response = await wrapAuth0Call<Credentials>({
        auth0Call: () =>
          getAuth0Credentials(scope, minTtl, parameters, shouldForceRefresh),
        errorContext,
        errorScenario,
      });

      if (
        response.error &&
        // do not logout if user is already logged out (e.g., on app cold start)
        response.error.type !== CredentialsManagerErrorCodes.NO_CREDENTIALS
      ) {
        await logout(LogoutReason.AuthError);
      }
      return response;
    },
    [getAuth0Credentials, logout],
  );

  const hasValidCredentials = useCallback(
    async (minTtl?: number) =>
      wrapAuth0Call({
        auth0Call: () => hasValidAuth0Credentials(minTtl),
      }),
    [hasValidAuth0Credentials],
  );

  const getAuthenticators = useCallback(
    async ({ mfaToken }: MfaGetAuthenticatorsParameters) =>
      wrapAuth0Call<MfaAuthenticator[]>({
        auth0Call: () => mfa.getAuthenticators({ mfaToken }),
      }),
    [mfa],
  );

  const mfaChallenge = useCallback(
    async ({ mfaToken }: MfaGetAuthenticatorsParameters) => {
      const mfaAuthenticators = await getAuthenticators({ mfaToken });
      const authenticatorId = getPreferredAuthenticator(
        mfaAuthenticators.data,
      )?.id;
      if (!authenticatorId) return;

      const response = await wrapAuth0Call<MfaChallengeResult>({
        auth0Call: () =>
          mfa.challenge({
            authenticatorId,
            mfaToken,
          }),
      });
      return response.data;
    },
    [getAuthenticators, mfa],
  );

  const handleCredentials = useCallback(
    async (data: Credentials) => {
      const decoded: Record<string, string> = jwtDecode(data.idToken);
      const decodedPasswordResetClaim = decoded[PasswordResetClaim];
      if (decodedPasswordResetClaim) {
        const response = await clearCredentials();
        setPasswordResetUrl(decodedPasswordResetClaim);
        return response;
      }

      try {
        await updateUserDeviceLoginState({
          isLoggedIn: true,
          os: Platform.OS,
          type: DeviceType.Mobile,
        });
      } catch (caughtErr) {
        captureException(caughtErr);
      }
      return { data };
    },
    [clearCredentials, setPasswordResetUrl, updateUserDeviceLoginState],
  );

  const onLoginResponse = (
    response: SerializableAuthResponse<Credentials | void>,
  ) => {
    setIsAuthenticating(false);
    return response;
  };

  const loginWithPasswordRealm = useCallback(
    async ({
      password,
      username,
    }: Pick<PasswordRealmParameters, "password" | "username">): Promise<
      SerializableAuthResponse<Credentials | void>
    > => {
      setIsAuthenticating(true);
      const loginResponse = await wrapAuth0Call({
        auth0Call: () =>
          loginWithAuth0PasswordRealm({
            audience: ProcessEnvironment.Auth0Audience,
            password,
            realm: ProcessEnvironment.Auth0PasswordRealm,
            scope: "openid profile email offline_access",
            username,
          }),
      });

      if (loginResponse.data) {
        const response = await handleCredentials(loginResponse.data);
        return onLoginResponse(response);
      }

      track(LoginAnalyticsEvent.FailedLogin, {
        [AnalyticsFieldName.Source]: LoginAnalyticsPageView.Login,
        [AnalyticsFieldName.Text]: getFailedLoginReason(loginResponse.error),
      });

      const mfaToken = loginResponse.json?.mfa_token;
      const userFacingErrorResponse = getUserFacingLoginErrorResponse(
        loginResponse.error,
      );
      if (!mfaToken) return onLoginResponse(userFacingErrorResponse);

      const mfaChallengeResult = await mfaChallenge({ mfaToken });
      const oobCode = mfaChallengeResult?.oobCode;
      if (!oobCode) return onLoginResponse(userFacingErrorResponse);

      setMFAToken(mfaToken);
      setOOBCode(oobCode);
      router.push(LoginScreenPath.MFA);
      return onLoginResponse({ data: undefined });
    },
    [
      track,
      mfaChallenge,
      setMFAToken,
      setOOBCode,
      loginWithAuth0PasswordRealm,
      handleCredentials,
    ],
  );

  const resetPassword = useCallback(
    (email: ResetPasswordParameters["email"]) =>
      wrapAuth0Call({
        auth0Call: () =>
          resetAuth0Password({
            connection: "my-equip",
            email,
          }),
      }),
    [resetAuth0Password],
  );

  const authorizeWithOOB = useCallback(
    async (
      bindingCode?: string,
    ): Promise<SerializableAuthResponse<Credentials | void>> => {
      if (
        isNil(bindingCode) ||
        isNil(mfaTokenFromState) ||
        isNil(oobCodeFromState)
      ) {
        return { data: undefined };
      }

      setIsAuthenticating(true);
      const response = await wrapAuth0Call({
        auth0Call: () =>
          authorizeWithAuth0OOB({
            bindingCode,
            mfaToken: mfaTokenFromState,
            oobCode: oobCodeFromState,
          }),
      });
      if (response.error) {
        setIsAuthenticating(false);
        return getUserFacingLoginErrorResponse(response.error);
      }

      await handleCredentials(response.data);
      resetMFAState();
      setIsAuthenticating(false);
      return response;
    },
    [
      authorizeWithAuth0OOB,
      handleCredentials,
      mfaTokenFromState,
      oobCodeFromState,
      resetMFAState,
    ],
  );

  const authorize = useCallback(
    () =>
      wrapAuth0Call({
        auth0Call: () => authorizeAuth0(WebAuth0AuthorizeProps),
        errorScenario: "LoginWeb: Auth0 Universal Login redirect failed",
      }),
    [authorizeAuth0],
  );

  const clearWebSession = useCallback(
    async (logoutReason: LogoutReason) => {
      if (!isWeb()) return;
      const shouldReturnToV2WebApp =
        // if user just arrived at v2 login page, do not route them back to v1.
        // only users getting irrevocably logged out should be sent back to v1's home page (InactivityTimeout, PressedLogout, etc)
        logoutReason === LogoutReason.AuthSessionExpiredAtStartup ||
        logoutReason === LogoutReason.JustPreLoginCleanup;

      // for all other logout reasons, DO route user to v1 (NotAcceptingLogins, InactivityTimeout, etc).
      const returnToUrl = shouldReturnToV2WebApp
        ? globalThis.window.location.origin
        : ProcessEnvironment.WebAppURL;

      // this redirects to Auth0 /v2/logout, so shortly after this executes no further app code will execute
      return await wrapAuth0Call({
        auth0Call: () => clearSession({ returnToUrl }),
        errorScenario: "Failure with Auth0 clearSession on web",
      });
    },
    [clearSession],
  );

  return {
    authorize,
    authorizeWithOOB,
    clearCredentials,
    clearWebSession,
    error,
    getAuthenticators,
    getCredentials,
    hasValidCredentials,
    isAuthenticated,
    isLoading: isAuthenticating || isLoading,
    loginWithPasswordRealm,
    logout,
    passwordResetUrl,
    preLoginCleanup,
    resetPassword,
  };
};

export default useAuth;
